/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from "react";

interface FaceDetectionItem {
  checkedAt: string;
  isFaceDetected: boolean;
  confidenceScore: number;
}

interface FaceDetectionProps {
  data: FaceDetectionItem[];
  disabledBanner?: React.ReactNode;
}

interface HeatmapCell {
  hour: number;
  minute: number;
  second: number;
  timestamp: string;
  isFaceDetected: boolean;
  confidenceScore: number;
}

interface DayData {
  date: string; // YYYY-MM-DD format
  displayDate: string; // "Feb 12, 2026"
  cells: HeatmapCell[]; // Array to show all detections, not grouped
  minHour: number;
  maxHour: number;
}

const FaceDetection: React.FC<FaceDetectionProps> = ({ data, disabledBanner }) => {
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const getLocalDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Automatically detect the interval from the data
  const detectedInterval = useMemo(() => {
    if (data.length < 2) return 5;

    const timestamps = data
      .map((item) => new Date(item.checkedAt).getTime())
      .sort((a, b) => a - b);

    // Calculate differences between consecutive timestamps
    const differences: number[] = [];
    for (let i = 1; i < timestamps.length && i < 10; i++) {
      differences.push(timestamps[i] - timestamps[i - 1]);
    }

    // Get the most common difference in minutes
    const avgDiff = differences.reduce((a, b) => a + b, 0) / differences.length;
    const intervalMinutes = Math.round(avgDiff / (1000 * 60));

    // Round to common intervals: 1, 5, 10, 15, 30, 60
    const commonIntervals = [1, 5, 10, 15, 30, 60];
    return commonIntervals.reduce((prev, curr) =>
      Math.abs(curr - intervalMinutes) < Math.abs(prev - intervalMinutes)
        ? curr
        : prev,
    );
  }, [data]);

  // Group data by date - show ALL detections individually
  const processDataByDate = (): Map<string, DayData> => {
    const dateMap = new Map<string, DayData>();

    data.forEach((item) => {
      // Ensure UTC timestamp is properly converted to local time
      const date = new Date(item.checkedAt);
      const dateKey = getLocalDateKey(date);
      const hour = date.getHours();
      const minute = date.getMinutes();
      const second = date.getSeconds();

      // Initialize day data if not exists
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          displayDate: date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          cells: [],
          minHour: 23,
          maxHour: 0,
        });
      }

      const dayData = dateMap.get(dateKey);

      // Removed console.warn to clean up the console, uncomment if needed for debugging
      // console.warn("Processing item:", {
      //   checkedAt: item.checkedAt,
      //   dateKey,
      //   hour,
      //   minute,
      //   second,
      // });

      if (!dayData) {
        return; // or throw error, or continue
      }

      // safe to use dayData here

      // Update min/max hours for this day
      dayData.minHour = Math.min(dayData.minHour, hour);
      dayData.maxHour = Math.max(dayData.maxHour, hour);

      // Add each detection as a separate cell
      dayData.cells.push({
        hour,
        minute,
        second,
        timestamp: item.checkedAt,
        isFaceDetected: item.isFaceDetected,
        confidenceScore: item.confidenceScore,
      });
    });

    // Sort cells by timestamp within each day
    dateMap.forEach((dayData) => {
      dayData.cells.sort((a, b) => {
        const timeA = a.hour * 3600 + a.minute * 60 + a.second;
        const timeB = b.hour * 3600 + b.minute * 60 + b.second;
        return timeA - timeB;
      });
    });

    return dateMap;
  };

  const dayDataMap = useMemo(
    () => processDataByDate(),
    [data, detectedInterval],
  );

  // Auto-expand first day on mount
  useMemo(() => {
    const sortedDates = Array.from(dayDataMap.keys()).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );
    if (sortedDates.length > 0) {
      setExpandedDays(new Set([sortedDates[0]]));
    }
  }, [dayDataMap]);

  // Get color based on confidence score and face detection
  const getCellColor = (cell: HeatmapCell | undefined): string => {
    if (!cell) return "bg-gray-100"; // No data
    if (!cell.isFaceDetected) return "bg-red-400"; // Face not detected - Red
    if (cell.isFaceDetected) return "bg-green-500"; // High confidence - Green
    return "bg-gray-100"; // Fallback
  };

  const formatTime = (
    hour: number,
    minute: number,
    second?: number,
  ): string => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    if (second !== undefined) {
      return `${String(displayHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")} ${period}`;
    }
    return `${String(displayHour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
  };

  const formatDateTime = (isoString: string) => {
    if (!isoString) return { date: "N/A", time: "N/A" };
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }),
    };
  };

  const handleMouseEnter = (
    cell: HeatmapCell | undefined,
    event: React.MouseEvent,
  ) => {
    if (cell) {
      setHoveredCell(cell);
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredCell(null);
  };

  const toggleDayExpansion = (dateKey: string) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  // Calculate statistics
  const totalDetections = data.length;
  const faceDetected = data.filter((item) => item.isFaceDetected).length;
  const notDetected = totalDetections - faceDetected;
  const avgConfidence =
    totalDetections > 0
      ? (data.reduce((sum, item) => sum + item.confidenceScore, 0) /
        totalDetections) *
      100
      : 0;

  // Sort dates in descending order (most recent first)
  const sortedDates = Array.from(dayDataMap.keys()).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <div className="space-y-4 px-2">
      {/* Header - Banner is now next to the title */}
      <div className="flex items-center gap-4 mb-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white shrink-0">
          Face Detection Heatmap
        </h2>
        {disabledBanner && (
          <div className="flex items-center">
            {disabledBanner}
          </div>
        )}
      </div>

      {/* Day-wise Heatmap Containers */}
      <div className="space-y-3">
        {sortedDates.map((dateKey) => {
          const dayData = dayDataMap.get(dateKey);
          if (!dayData) {
            return null; // or skip rendering
          }
          const isExpanded = expandedDays.has(dateKey);
          const dayDetections = data.filter((item) => {
            const date = new Date(item.checkedAt);
            return getLocalDateKey(date) === dateKey;
          });
          const dayFaceDetected = dayDetections.filter(
            (item) => item.isFaceDetected,
          ).length;

          // Generate hours array for this specific day based on actual data
          const workHours = Array.from(
            { length: dayData.maxHour - dayData.minHour + 1 },
            (_, i) => dayData.minHour + i,
          );

          // Group cells by hour for display
          const cellsByHour = new Map<number, HeatmapCell[]>();
          dayData.cells.forEach((cell) => {
            if (!cellsByHour.has(cell.hour)) {
              cellsByHour.set(cell.hour, []);
            }
            cellsByHour.get(cell.hour)?.push(cell);
          });

          return (
            <div
              key={dateKey}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Day Header - Clickable */}
              <button
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 cursor-pointer hover:from-blue-100 hover:to-indigo-100 dark:hover:from-gray-600 dark:hover:to-gray-700 transition-colors"
                onClick={() => toggleDayExpansion(dateKey)}
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {dayData.displayDate}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {dayDetections.length} detections •{" "}
                      {formatTime(dayData.minHour, 0)} -{" "}
                      {formatTime(dayData.maxHour + 1, 0)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Detection Rate</div>
                    <div className="text-lg font-bold text-green-600 dark:text-green-400">
                      {dayDetections.length > 0
                        ? (
                          (dayFaceDetected / dayDetections.length) *
                          100
                        ).toFixed(1)
                        : 0}
                      %
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Detected</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {dayFaceDetected}/{dayDetections.length}
                    </div>
                  </div>
                </div>
              </button>

              {/* Heatmap Content - Collapsible */}
              {isExpanded && (
                <div className="p-4">
                  <div className="space-y-3">
                    {workHours.map((hour) => {
                      const hourCells = cellsByHour.get(hour) || [];
                      if (hourCells.length === 0) return null;

                      return (
                        <div
                          key={hour}
                          className="border border-gray-200 dark:border-gray-600 rounded-lg p-3"
                        >
                          <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                            {formatTime(hour, 0)} - {formatTime(hour + 1, 0)}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {hourCells.map((cell, index) => (
                              <button
                                type="button"
                                key={`${cell.hour}-${cell.minute}-${cell.second}-${index}`}
                                className={`${getCellColor(cell)} rounded px-3 py-2 hover:ring-2 hover:ring-blue-500 transition-all shadow-sm text-left`}
                                onMouseEnter={(e) => handleMouseEnter(cell, e)}
                                onMouseLeave={handleMouseLeave}
                              >
                                <div className="text-xs font-semibold text-gray-900">
                                  {formatTime(
                                    cell.hour,
                                    cell.minute,
                                    cell.second,
                                  )}
                                </div>
                                <div className="text-xs text-gray-700 mt-1">
                                  {(cell.confidenceScore * 100).toFixed(1)}%
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {sortedDates.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No data available
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No face detection records found for the selected date range.
          </p>
        </div>
      )}

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="fixed z-50 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm pointer-events-none"
          style={{
            left: `${tooltipPosition.x + 10}px`,
            top: `${tooltipPosition.y + 10}px`,
          }}
        >
          <div className="space-y-1">
            <div className="font-semibold">
              {formatDateTime(hoveredCell.timestamp).time}
            </div>
            <div className="text-xs text-gray-300">
              {formatDateTime(hoveredCell.timestamp).date}
            </div>
            <div className="text-xs space-y-0.5 mt-2">
              <div>
                Face Detected:{" "}
                <span
                  className={
                    hoveredCell.isFaceDetected
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  {hoveredCell.isFaceDetected ? "Yes" : "No"}
                </span>
              </div>
              <div>
                Confidence:{" "}
                <span
                  className={
                    hoveredCell.confidenceScore >= 0.8
                      ? "text-green-400"
                      : hoveredCell.confidenceScore >= 0.5
                        ? "text-yellow-400"
                        : "text-orange-400"
                  }
                >
                  {(hoveredCell.confidenceScore * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Detections</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {totalDetections}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Face Detected</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {faceDetected}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Not Detected</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
            {notDetected}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Avg Confidence</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {avgConfidence.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceDetection;